import { useEffect } from "react";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import "../../styles/home.css";
import "../../styles/how-prufyy-works.css";
import { initScrollReveal } from "../../utils/scrollReveal";
import { initScrollProgress } from "../../utils/scrollProgress";

function VC() {
  useEffect(() => {
    initScrollReveal();
    initScrollProgress();
  }, []);

  return (
    <>
      <Header />

      {/* SCROLL PROGRESS */}
      <div className="scroll-progress">
        <div className="scroll-progress-fill" />
      </div>

      <main className="hpw-wrapper">
        {/* ================= HERO ================= */}
        <section className="hero-section reveal">
          <div className="hero-content">
            <h1>
              Venture Decisions Are Institutional <br />
              Not Intuitive
            </h1>
            <p>
              Venture capital decisions persist beyond individuals. Every memo,
              review, and IC discussion becomes part of a firm’s institutional
              record — and must be defensible over time.
            </p>
          </div>
        </section>

        {/* ================= AUDIENCE ================= */}
        <section className="section reveal">
          <span className="audience-badge">VC Perspective</span>
          <h2>For Venture Capital Firms Evaluating at Scale</h2>
          <p>
            Venture firms assess hundreds of startups across analysts,
            principals, and partners. Clarity, consistency, and accountability
            matter more than speed alone.
          </p>
        </section>

        {/* ================= PROBLEM ================= */}
        <section className="section reveal">
          <h2>Where Venture Signal Breaks Down</h2>

          <div className="info-cards">
            <div className="info-card">
              <h4>Memo Inconsistency</h4>
              <p>Analyst memos vary in structure and depth.</p>
            </div>

            <div className="info-card">
              <h4>Compressed Narratives</h4>
              <p>Claims get flattened into summaries.</p>
            </div>

            <div className="info-card">
              <h4>Detached Evidence</h4>
              <p>Proof is separated from assertions.</p>
            </div>

            <div className="info-card">
              <h4>Reviewer Bias</h4>
              <p>Opinions leak across teams.</p>
            </div>

            <div className="info-card">
              <h4>Lost Context</h4>
              <p>Diligence history fades over time.</p>
            </div>
          </div>
        </section>

        {/* ================= NEED ================= */}
        <section className="section reveal">
          <h2>What Venture Firms Actually Need</h2>

          <div className="info-cards">
            <div className="info-card">
              <h4>Claim-Level Structure</h4>
              <p>Clear, consistent diligence framing.</p>
            </div>

            <div className="info-card">
              <h4>Evidence Traceability</h4>
              <p>Know exactly what supports each claim.</p>
            </div>

            <div className="info-card">
              <h4>Independent Reviews</h4>
              <p>Analysts evaluate without influence.</p>
            </div>

            <div className="info-card">
              <h4>IC Defensibility</h4>
              <p>Decisions that hold up in committee.</p>
            </div>

            <div className="info-card">
              <h4>Institutional Memory</h4>
              <p>Portfolio learning persists over years.</p>
            </div>

            <div className="info-card">
              <h4>Controlled Sharing</h4>
              <p>External access without exposure.</p>
            </div>
          </div>
        </section>

        {/* ================= STRUCTURE ================= */}
        <section className="section reveal">
          <h2>From Memos to Verifiable Judgment</h2>

          <div className="flow-diagram">
            <div className="flow-step">
              <div className="flow-icon">📥</div>
              <h3>Deal Intake</h3>
              <p>Startups enter the pipeline.</p>
            </div>

            <div className="flow-step">
              <div className="flow-icon">🧩</div>
              <h3>Claims Isolated</h3>
              <p>Key assertions are structured.</p>
            </div>

            <div className="flow-step">
              <div className="flow-icon">📎</div>
              <h3>Evidence Attached</h3>
              <p>Proof is linked where scrutiny exists.</p>
            </div>

            <div className="flow-step">
              <div className="flow-icon">🏛️</div>
              <h3>IC-Ready Clarity</h3>
              <p>Judgment is defensible, not assumed.</p>
            </div>
          </div>
        </section>

        {/* ================= EXAMPLE ================= */}
        <section className="section reveal">
          <h2>Example: IC Preparation Without Guesswork</h2>

          <p>
            Instead of hunting for documents during IC discussions, partners
            review claims and supporting evidence in place — focusing the
            conversation on judgment, not logistics.
          </p>

          <div className="claim-evidence">
            <div className="claim-box">
              <strong>Claim</strong>
              <p>“The team has a defensible technical advantage.”</p>
            </div>

            <div className="link-line" />

            <div className="evidence-box">
              <strong>Evidence</strong>
              <ul>
                <li>Architecture documentation</li>
                <li>Technical benchmarks</li>
                <li>Prior implementation history</li>
              </ul>
            </div>
          </div>
        </section>

        {/* ================= MULTI-REVIEWER ================= */}
        <section className="section reveal">
          <h2>Multi-Reviewer Input Without Contamination</h2>

          <div className="info-cards">
            <div className="info-card">
              <h4>Independent Analysts</h4>
              <p>Reviews happen in isolation.</p>
            </div>

            <div className="info-card">
              <h4>No Draft Visibility</h4>
              <p>Opinions stay private.</p>
            </div>

            <div className="info-card">
              <h4>Locked Submissions</h4>
              <p>Accountability without pressure.</p>
            </div>

            <div className="info-card">
              <h4>No Forced Consensus</h4>
              <p>Judgment emerges naturally.</p>
            </div>
          </div>
        </section>

        {/* ================= TRACEABILITY ================= */}
        <section className="section reveal">
          <h2>Decision Traceability Over Time</h2>

          <div className="info-cards">
            <div className="info-card">
              <h4>Decision Rationale</h4>
              <p>Why a decision was made.</p>
            </div>

            <div className="info-card">
              <h4>Claim Relevance</h4>
              <p>Which assertions mattered.</p>
            </div>

            <div className="info-card">
              <h4>Evidence Record</h4>
              <p>What supported the decision.</p>
            </div>

            <div className="info-card">
              <h4>Learning Integrity</h4>
              <p>Improve without hindsight distortion.</p>
            </div>
          </div>
        </section>

        {/* ================= GOVERNANCE ================= */}
        <section className="section reveal">
          <h2>Built for Institutional Safety</h2>

          <div className="info-cards">
            <div className="info-card">
              <h4>No Public Discovery</h4>
              <p>Startups remain private.</p>
            </div>

            <div className="info-card">
              <h4>No Rankings</h4>
              <p>Credibility is not gamified.</p>
            </div>

            <div className="info-card">
              <h4>No AI Verdicts</h4>
              <p>Human judgment stays central.</p>
            </div>

            <div className="info-card">
              <h4>Revocable Access</h4>
              <p>Sharing can be withdrawn anytime.</p>
            </div>

            <div className="info-card">
              <h4>Regulator-Safe</h4>
              <p>Designed for institutional scrutiny.</p>
            </div>
          </div>
        </section>

        {/* ================= FINAL CTA ================= */}
        <section className="final-cta-section reveal">
          <h2>Bring Structure to Your IC Process</h2>
          <p>
            Prufyy helps venture firms preserve judgment, consistency, and
            accountability across every decision.
          </p>
          <a href="/signup" className="btn-primary">
            Get Started with Prufyy
          </a>
        </section>
      </main>

      <Footer />
    </>
  );
}

export default VC;
